/**
 * Admin-Seite zur Verwaltung von Standorten und ihren Schicht-Vorlagen.
 *
 * Layout: Liste der Standorte als ausklappbare Karten. Pro Standort eine
 * Sub-Liste der Vorlagen. Neue Standorte/Vorlagen werden inline angelegt.
 */

import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth, useCanManage, useCanManageLocation, useManagedLocationIds } from '../store/auth';
import {
  BUNDESLAND_LABELS,
  locationsApi,
  templatesApi,
  WEEKDAY_LABELS,
  WEEKDAY_ORDER,
  type ApiLocation,
  type ApiShiftTemplate,
  type Bundesland,
  type OpeningHoursMap,
  type WeekdayKey,
} from '../lib/locations';
import { ApiError } from '../lib/api';
import { companiesApi } from '../lib/companies';
import { regionsApi, type ApiRegion } from '../lib/regions';

const LOCATIONS_QK = ['locations', 'withTemplates'] as const;

export default function LocationsAdminPage() {
  const token = useAuth((s) => s.token)!;
  const qc = useQueryClient();
  // Standort-Stammdaten (Anlegen/Bearbeiten/Löschen eines Standorts selbst)
  // bleiben Chef/Owner-exklusiv (spiegelt requireCompanyOwner in locations.ts).
  // Filialleitung darf hier nur ihre EIGENEN Standorte sehen und ausschließlich
  // deren Vorlagen pflegen (spiegelt shiftTemplates.ts: Owner überall,
  // Filialleiter nur am eigenen Standort) — siehe Kapitel 20 "bewusst offen
  // gelassener Punkt" aus der Phase-A-Session.
  const isOwner = useCanManage();
  const managedLocationIds = useManagedLocationIds();

  const { data: locations, isLoading, error } = useQuery({
    queryKey: LOCATIONS_QK,
    queryFn: () => locationsApi.list(token, true),
  });

  const visibleLocations = isOwner
    ? locations
    : locations?.filter((loc) => managedLocationIds.includes(loc.id));

  // v1.3.0: Deep-Link „+ Filiale" vom Chef-Dashboard (`?neu=1`) öffnet das
  // Anlegen-Formular direkt, statt erst die Liste zu zeigen.
  const [searchParams, setSearchParams] = useSearchParams();
  const [showCreate, setShowCreate] = useState(() => searchParams.get('neu') === '1');
  function closeCreate() {
    setShowCreate(false);
    if (searchParams.has('neu')) {
      const next = new URLSearchParams(searchParams);
      next.delete('neu');
      setSearchParams(next, { replace: true });
    }
  }

  return (
    <div className="max-w-2xl lg:max-w-5xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">Standorte & Vorlagen</h2>
        {isOwner && (
          <button
            onClick={() => setShowCreate(true)}
            className="bg-brand hover:bg-brand-dark transition rounded px-3 py-2 text-sm"
          >
            + Filiale anlegen
          </button>
        )}
      </div>

      {/* Screen 1 (Design-Canvas „Chef Standorte-Übersicht"): Querlinks zu den
          firmenweiten Chef-Funktionen, damit man nicht erst übers Dashboard muss. */}
      {isOwner && (
        <nav className="flex flex-wrap gap-2" aria-label="Chef-Funktionen">
          <Link to="/team" className="btn-ghost text-xs px-3 py-1.5 !min-h-0">
            Lohnrechnung &amp; Vorschüsse
          </Link>
          <Link to="/wochenplan" className="btn-ghost text-xs px-3 py-1.5 !min-h-0">
            Schichtplan
          </Link>
          <Link to="/meine-ansicht" className="btn-ghost text-xs px-3 py-1.5 !min-h-0">
            Meine Ansicht
          </Link>
        </nav>
      )}

      {!isOwner && (
        <p className="text-xs text-slate-500">
          Als Filialleitung siehst du hier nur deine eigenen Standorte und kannst deren
          Vorlagen pflegen. Standort-Stammdaten verwaltet der Chef.
        </p>
      )}

      {isLoading && <p className="text-slate-400">Lade…</p>}
      {error && <p className="text-red-400">Fehler: {(error as Error).message}</p>}

      {!isLoading && visibleLocations?.length === 0 && (
        <p className="text-slate-500 text-sm">Keine Standorte zugeordnet.</p>
      )}

      <ul className="space-y-3 lg:grid lg:grid-cols-2 lg:gap-3 lg:space-y-0">
        {visibleLocations?.map((loc) => (
          <LocationCard key={loc.id} location={loc} onChanged={() => qc.invalidateQueries({ queryKey: LOCATIONS_QK })} />
        ))}
      </ul>

      {showCreate && isOwner && (
        <LocationModal
          mode="create"
          onClose={closeCreate}
          onSaved={() => {
            closeCreate();
            qc.invalidateQueries({ queryKey: LOCATIONS_QK });
            // Dashboard/Standort-Kacheln sofort aktualisieren.
            qc.invalidateQueries({ queryKey: ['dashboard'] });
            qc.invalidateQueries({ queryKey: ['locations'] });
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function LocationCard({
  location,
  onChanged,
}: {
  location: ApiLocation;
  onChanged: () => void;
}) {
  const token = useAuth((s) => s.token)!;
  const isOwner = useCanManage();
  const canManageTemplates = useCanManageLocation(location.id);
  const [editing, setEditing] = useState(false);
  const [showAddTemplate, setShowAddTemplate] = useState(false);
  const { data: companies } = useQuery({ queryKey: ['companies'], queryFn: () => companiesApi.list(token) });
  const companyName = companies?.find((c) => c.id === location.companyId)?.name;
  const { data: regions } = useQuery({ queryKey: ['regions'], queryFn: () => regionsApi.list(token) });
  const regionName = regions?.find((r) => r.id === location.regionId)?.name;

  const removeMutation = useMutation({
    mutationFn: () => locationsApi.remove(token, location.id),
    onSuccess: onChanged,
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        alert(err.message);
      } else if (err instanceof Error) {
        alert(err.message);
      }
    },
  });

  return (
    <li
      className="bg-slate-800 rounded-lg overflow-hidden border-l-4"
      style={{ borderLeftColor: location.color }}
    >
      <div className="p-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium">
            {location.name}
            {!location.isActive && <span className="ml-2 text-xs text-amber-400">(inaktiv)</span>}
          </p>
          <p className="text-xs text-slate-400">
            {companyName ? `${companyName} · ` : ''}
            {regionName ? `${regionName} · ` : ''}
            {location.templates?.length ?? 0} Vorlage(n)
          </p>
          {location.address && <p className="text-xs text-slate-500 truncate">{location.address}</p>}
          {location.bundesland && (
            <p className="text-xs text-slate-500">{BUNDESLAND_LABELS[location.bundesland]}</p>
          )}
        </div>
        {isOwner && (
          <div className="flex flex-col gap-1 shrink-0">
            <button
              onClick={() => setEditing(true)}
              className="text-xs bg-slate-700 hover:bg-slate-600 rounded px-2 py-1"
            >
              Bearbeiten
            </button>
            <button
              onClick={() => {
                if (confirm(`Standort "${location.name}" löschen?`)) removeMutation.mutate();
              }}
              disabled={removeMutation.isPending}
              className="text-xs bg-red-600/80 hover:bg-red-600 rounded px-2 py-1 disabled:opacity-50"
            >
              Löschen
            </button>
          </div>
        )}
      </div>

      <div className="border-t border-slate-700 p-3">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm uppercase text-slate-400">Vorlagen</h4>
          {canManageTemplates && (
            <button
              onClick={() => setShowAddTemplate(true)}
              className="text-xs bg-slate-700 hover:bg-slate-600 rounded px-2 py-1"
            >
              + Vorlage
            </button>
          )}
        </div>
        <ul className="space-y-1">
          {location.templates?.length ? (
            location.templates.map((t) => (
              <TemplateRow key={t.id} template={t} onChanged={onChanged} canManage={canManageTemplates} />
            ))
          ) : (
            <li className="text-sm text-slate-500">Keine Vorlagen vorhanden.</li>
          )}
        </ul>
      </div>

      {isOwner && editing && (
        <LocationModal
          mode="edit"
          location={location}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            onChanged();
          }}
        />
      )}
      {showAddTemplate && (
        <TemplateModal
          mode="create"
          locationId={location.id}
          onClose={() => setShowAddTemplate(false)}
          onSaved={() => {
            setShowAddTemplate(false);
            onChanged();
          }}
        />
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------

function TemplateRow({
  template,
  onChanged,
  canManage,
}: {
  template: ApiShiftTemplate;
  onChanged: () => void;
  /** Owner ODER Filialleitung genau an diesem Standort — spiegelt shiftTemplates.ts. */
  canManage: boolean;
}) {
  const token = useAuth((s) => s.token)!;
  const [editing, setEditing] = useState(false);

  const removeMutation = useMutation({
    mutationFn: () => templatesApi.remove(token, template.id),
    onSuccess: (data) => {
      if (data.affectedShifts > 0) {
        alert(`Vorlage gelöscht. ${data.affectedShifts} Schicht(en) verlieren die Vorlagen-Verknüpfung (bleiben unverändert).`);
      }
      onChanged();
    },
  });

  return (
    <li className="flex items-center justify-between bg-slate-900/40 rounded px-2 py-1">
      <div className="text-sm">
        <span className="font-medium">{template.name}</span>{' '}
        <span className="text-slate-400">
          {template.isOpenEnd ? `ab ${template.startTime}` : `${template.startTime}–${template.endTime}`}
        </span>
        {template.isOpenEnd && (
          <span className="ml-2 text-xs text-amber-300">Var. Ende</span>
        )}
        {!template.isActive && <span className="ml-2 text-xs text-amber-400">(inaktiv)</span>}
        {template.usageCount != null && (
          <span
            className="ml-2 text-xs text-slate-500"
            title="Anzahl Schichten, die diese Vorlage nutzen (Phase Y)"
          >
            · {template.usageCount}×
          </span>
        )}
      </div>
      {canManage && (
        <div className="flex gap-1">
          <button
            onClick={() => setEditing(true)}
            className="text-xs bg-slate-700 hover:bg-slate-600 rounded px-2 py-1"
          >
            ✎
          </button>
          <button
            onClick={() => {
              if (confirm(`Vorlage "${template.name}" löschen?`)) removeMutation.mutate();
            }}
            className="text-xs bg-red-600/80 hover:bg-red-600 rounded px-2 py-1 disabled:opacity-50"
            disabled={removeMutation.isPending}
          >
            ×
          </button>
        </div>
      )}

      {canManage && editing && (
        <TemplateModal
          mode="edit"
          template={template}
          locationId={template.locationId}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            onChanged();
          }}
        />
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------

interface LocationModalProps {
  mode: 'create' | 'edit';
  location?: ApiLocation;
  onClose: () => void;
  onSaved: () => void;
}

export function LocationModal({ mode, location, onClose, onSaved }: LocationModalProps) {
  const token = useAuth((s) => s.token)!;
  const me = useAuth((s) => s.user);
  const activeCompanyId = useAuth((s) => s.activeCompanyId);
  const meIsGlobalAdmin = me?.role === 'admin' || me?.role === 'superadmin';
  const qc = useQueryClient();
  const [name, setName] = useState(location?.name ?? '');
  const [color, setColor] = useState(location?.color ?? '#3b82f6');
  const [isActive, setIsActive] = useState(location?.isActive ?? true);
  // v1.3.0: neue Filiale landet standardmäßig in der aktiven Firma — der
  // Chef muss nichts auswählen (die Firma verschieben kann ohnehin nur ein Admin).
  const [companyId, setCompanyId] = useState<string>(location?.companyId ?? activeCompanyId ?? '');
  const [address, setAddress] = useState(location?.address ?? '');
  const [branchNumber, setBranchNumber] = useState(location?.branchNumber ?? '');
  const [managerCanSeeWages, setManagerCanSeeWages] = useState(location?.managerCanSeeWages ?? false);
  const [managerCanSeeBonusHours, setManagerCanSeeBonusHours] = useState(
    location?.managerCanSeeBonusHours ?? false,
  );
  const [allowDoubleShift, setAllowDoubleShift] = useState(location?.allowDoubleShift ?? false);
  const [allowCrossLocationSwap, setAllowCrossLocationSwap] = useState(
    location?.allowCrossLocationSwap ?? false,
  );
  const [regionId, setRegionId] = useState<string>(location?.regionId ?? '');
  const [bundesland, setBundesland] = useState<Bundesland | ''>(location?.bundesland ?? '');
  const [openingHours, setOpeningHours] = useState<OpeningHoursMap>(
    (location?.openingHours as OpeningHoursMap | null) ?? {},
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { data: companies } = useQuery({ queryKey: ['companies'], queryFn: () => companiesApi.list(token) });

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (!companyId) {
        setError('Bitte eine Firma wählen.');
        setBusy(false);
        return;
      }
      const hasOpeningHours = WEEKDAY_ORDER.some((d) => openingHours[d]);
      const payload = {
        name,
        color,
        isActive,
        // v1.3.0: Firma nur mitschicken, wenn sie sich wirklich ändert (bzw.
        // beim Anlegen) — sonst lehnte das Backend das Speichern des Chefs ab.
        ...(mode === 'create' || companyId !== location?.companyId ? { companyId } : {}),
        address: address.trim() || null,
        branchNumber: branchNumber.trim() || null,
        openingHours: hasOpeningHours ? openingHours : null,
        managerCanSeeWages,
        managerCanSeeBonusHours,
        allowDoubleShift,
        allowCrossLocationSwap,
        regionId: regionId || null,
        bundesland: bundesland || null,
      };
      if (mode === 'create') {
        await locationsApi.create(token, payload);
      } else if (location) {
        await locationsApi.update(token, location.id, payload);
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={mode === 'create' ? 'Neuer Standort' : 'Standort bearbeiten'} onClose={onClose}>
      <form onSubmit={onSubmit} className="space-y-3">
        <Field label="Name">
          <input required value={name} onChange={(e) => setName(e.target.value)} className="input" />
        </Field>
        {meIsGlobalAdmin || !companyId ? (
          <Field label="Firma">
            <select
              required
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value)}
              className="input"
            >
              <option value="" disabled>— Firma wählen —</option>
              {companies?.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </Field>
        ) : (
          <Field label="Firma">
            <p className="text-sm text-slate-300 px-1 py-1.5">
              {companies?.find((c) => c.id === companyId)?.name ?? 'Aktive Firma'}
            </p>
          </Field>
        )}
        <Field label="Adresse (optional)">
          <input
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            className="input"
            placeholder="Straße, PLZ Ort"
          />
        </Field>
        <Field label="Filialnummer (optional)">
          <input
            value={branchNumber}
            onChange={(e) => setBranchNumber(e.target.value)}
            className="input"
            placeholder="z. B. für den Abgleich mit dem Kassensystem"
          />
        </Field>
        <RegionField
          token={token}
          companyId={companyId}
          value={regionId}
          onChange={setRegionId}
          onRegionCreated={() => qc.invalidateQueries({ queryKey: ['regions'] })}
        />
        <Field label="Bundesland (optional)">
          <select
            value={bundesland}
            onChange={(e) => setBundesland(e.target.value as Bundesland | '')}
            className="input"
          >
            <option value="">— keine automatische Feiertags-Erkennung —</option>
            {(Object.keys(BUNDESLAND_LABELS) as Bundesland[]).map((code) => (
              <option key={code} value={code}>
                {BUNDESLAND_LABELS[code]}
              </option>
            ))}
          </select>
          <p className="text-[11px] text-slate-500 mt-1">
            Grundlage für die automatische Feiertags-Erkennung im Schichtplan
            (Kapitel 19.3). Ohne Angabe kein Hinweis-Badge — z. B. bei
            Standorten außerhalb Deutschlands.
          </p>
        </Field>
        <Field label="Farbe">
          <input
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            className="w-16 h-10 bg-transparent border border-slate-700 rounded"
          />
        </Field>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
          />
          Aktiv
        </label>
        <OpeningHoursField value={openingHours} onChange={setOpeningHours} />
        <div className="border-t border-slate-700 pt-3 space-y-2">
          <p className="text-xs uppercase text-slate-400">Standort-Rechte</p>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={managerCanSeeWages}
              onChange={(e) => setManagerCanSeeWages(e.target.checked)}
            />
            Filialleitung darf hier Löhne sehen (Entscheidung #6)
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={managerCanSeeBonusHours}
              onChange={(e) => setManagerCanSeeBonusHours(e.target.checked)}
            />
            Filialleitung darf hier Einspringen-Zusatzstunden sehen (Entscheidung #16, unabhängig von Löhnen)
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={allowDoubleShift}
              onChange={(e) => setAllowDoubleShift(e.target.checked)}
            />
            Doppelbuchung am selben Tag erlauben
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={allowCrossLocationSwap}
              onChange={(e) => setAllowCrossLocationSwap(e.target.checked)}
            />
            Schichttausch mit anderen Standorten erlauben
          </label>
        </div>
        {error && <p className="text-sm text-red-400">{error}</p>}
        <ModalFooter onClose={onClose} busy={busy} />
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------

/**
 * Öffnungszeiten-Feld im Standort-Formular (Kapitel 2/19.4) — bewusst optional
 * und rein informativ: dient später nur als nicht-blockierender Warnhinweis im
 * Schichtplan (Kapitel 19.4, "außerhalb der Öffnungszeiten"), keine Sperre.
 * Ein Wochentag ohne aktiviertes Häkchen bedeutet "geschlossen" (kein Eintrag
 * im JSON, nicht `{ open: '', close: '' }`).
 */
function OpeningHoursField({
  value,
  onChange,
}: {
  value: OpeningHoursMap;
  onChange: (next: OpeningHoursMap) => void;
}) {
  function setDay(day: WeekdayKey, open: boolean) {
    if (!open) {
      const next = { ...value };
      delete next[day];
      onChange(next);
      return;
    }
    onChange({ ...value, [day]: value[day] ?? { open: '09:00', close: '18:00' } });
  }

  function setTime(day: WeekdayKey, field: 'open' | 'close', time: string) {
    const current = value[day] ?? { open: '09:00', close: '18:00' };
    onChange({ ...value, [day]: { ...current, [field]: time } });
  }

  return (
    <div className="border-t border-slate-700 pt-3 space-y-2">
      <p className="text-xs uppercase text-slate-400">Öffnungszeiten (optional)</p>
      <div className="space-y-1">
        {WEEKDAY_ORDER.map((day) => {
          const entry = value[day];
          return (
            <div key={day} className="flex items-center gap-2 text-sm">
              <label className="flex items-center gap-2 w-28 shrink-0">
                <input
                  type="checkbox"
                  checked={!!entry}
                  onChange={(e) => setDay(day, e.target.checked)}
                />
                {WEEKDAY_LABELS[day]}
              </label>
              {entry ? (
                <>
                  <input
                    type="time"
                    value={entry.open}
                    onChange={(e) => setTime(day, 'open', e.target.value)}
                    className="input !py-1 !px-2 w-28"
                  />
                  <span className="text-slate-500">–</span>
                  <input
                    type="time"
                    value={entry.close}
                    onChange={(e) => setTime(day, 'close', e.target.value)}
                    className="input !py-1 !px-2 w-28"
                  />
                </>
              ) : (
                <span className="text-slate-600 text-xs">geschlossen</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Region-Feld im Standort-Formular (Kapitel 20.2) — bewusst KEIN eigener
 * CRUD-Screen. Dropdown mit den Regionen der gewählten Firma, plus
 * "+ Neue Region…" (Freitext, legt sofort eine neue Region an) und
 * "— keine —" als Default.
 */
function RegionField({
  token,
  companyId,
  value,
  onChange,
  onRegionCreated,
}: {
  token: string;
  companyId: string;
  value: string;
  onChange: (regionId: string) => void;
  onRegionCreated: () => void;
}) {
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { data: regions } = useQuery({
    queryKey: ['regions'],
    queryFn: () => regionsApi.list(token),
    enabled: !!companyId,
  });
  const companyRegions = regions?.filter((r: ApiRegion) => r.companyId === companyId) ?? [];

  async function createRegion() {
    if (!newName.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const region = await regionsApi.create(token, newName.trim());
      onRegionCreated();
      onChange(region.id);
      setCreating(false);
      setNewName('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Region konnte nicht angelegt werden');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Field label="Region (optional)">
      {!creating ? (
        <select
          value={value}
          onChange={(e) => {
            if (e.target.value === '__new__') {
              setCreating(true);
            } else {
              onChange(e.target.value);
            }
          }}
          className="input"
          disabled={!companyId}
        >
          <option value="">— keine —</option>
          {companyRegions.map((r) => (
            <option key={r.id} value={r.id}>{r.name}</option>
          ))}
          <option value="__new__">+ Neue Region…</option>
        </select>
      ) : (
        <div className="flex gap-2">
          <input
            autoFocus
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                createRegion();
              }
            }}
            placeholder="Name der Region"
            className="input flex-1"
          />
          <button
            type="button"
            onClick={createRegion}
            disabled={busy || !newName.trim()}
            className="text-xs bg-brand hover:bg-brand-dark rounded px-3 disabled:opacity-50"
          >
            Anlegen
          </button>
          <button
            type="button"
            onClick={() => {
              setCreating(false);
              setNewName('');
              setError(null);
            }}
            className="text-xs bg-slate-700 hover:bg-slate-600 rounded px-3"
          >
            Abbrechen
          </button>
        </div>
      )}
      {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
    </Field>
  );
}

// ---------------------------------------------------------------------------

interface TemplateModalProps {
  mode: 'create' | 'edit';
  template?: ApiShiftTemplate;
  locationId: string;
  onClose: () => void;
  onSaved: () => void;
}

function TemplateModal({ mode, template, locationId, onClose, onSaved }: TemplateModalProps) {
  const token = useAuth((s) => s.token)!;
  const [name, setName] = useState(template?.name ?? '');
  const [startTime, setStartTime] = useState(template?.startTime ?? '08:00');
  const [endTime, setEndTime] = useState(template?.endTime ?? '14:00');
  const [isActive, setIsActive] = useState(template?.isActive ?? true);
  const [isOpenEnd, setIsOpenEnd] = useState(template?.isOpenEnd ?? false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (mode === 'create') {
        await templatesApi.create(token, {
          locationId,
          name,
          startTime,
          endTime,
          isActive,
          isOpenEnd,
        });
      } else if (template) {
        await templatesApi.update(token, template.id, {
          name,
          startTime,
          endTime,
          isActive,
          isOpenEnd,
        });
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={mode === 'create' ? 'Neue Vorlage' : 'Vorlage bearbeiten'} onClose={onClose}>
      <form onSubmit={onSubmit} className="space-y-3">
        <Field label="Name">
          <input required value={name} onChange={(e) => setName(e.target.value)} className="input" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Start">
            <input
              required
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="input"
            />
          </Field>
          <Field label={isOpenEnd ? 'Ende (Platzhalter)' : 'Ende'}>
            <input
              required={!isOpenEnd}
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className="input"
            />
          </Field>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
          />
          Aktiv
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={isOpenEnd}
            onChange={(e) => setIsOpenEnd(e.target.checked)}
          />
          Variable Endzeit (z. B. „Ware") — Mitarbeiter trägt nach
        </label>
        {error && <p className="text-sm text-red-400">{error}</p>}
        <ModalFooter onClose={onClose} busy={busy} />
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// kleine Hilfs-Komponenten

function Modal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-slate-800 rounded-t-2xl sm:rounded-lg p-4 sm:p-5 w-full max-w-md max-h-[92vh] sm:max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold mb-4">{title}</h3>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-sm text-slate-300 mb-1">{label}</span>
      {children}
    </label>
  );
}

function ModalFooter({ onClose, busy }: { onClose: () => void; busy: boolean }) {
  return (
    <div className="flex justify-end gap-2 pt-2">
      <button
        type="button"
        onClick={onClose}
        className="px-3 py-2 text-sm rounded bg-slate-700 hover:bg-slate-600"
      >
        Abbrechen
      </button>
      <button
        type="submit"
        disabled={busy}
        className="px-3 py-2 text-sm rounded bg-brand hover:bg-brand-dark disabled:opacity-50"
      >
        {busy ? 'Speichere…' : 'Speichern'}
      </button>
    </div>
  );
}
